import { EMPTY, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AppVersionResult } from '../model/app-version-result.model';
import { AuthenticatedUser } from '../model/authentication/authenticated-user.model';
import { APP_UPDATE, IAppUpdate } from '../model/pacificwise-angular-config.model';
import { TokenResult } from '../model/token-result.model';
import { ApiConfigService } from './api-config.service';
import { AuthenticationService } from './authentication.service';
import { DialogMessage } from './dialog-message.service';
import { LocalStorageService } from './local-storage.service';

@Injectable()
export class UserCredentialService {
  private loggedoutSubject = new Subject<AuthenticatedUser>();
  loggedout$ = this.loggedoutSubject.asObservable();

  private refreshToken$: Observable<TokenResult>;

  constructor(
    private httpClient: HttpClient,
    private apiConfigService: ApiConfigService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private dialogMessage: DialogMessage,
    @Inject(APP_UPDATE) private appUpdate: IAppUpdate
  ) { }

  login(user: AuthenticatedUser, rememberMe: boolean): Observable<TokenResult> {
    return this.httpClient.post<TokenResult>(this.apiConfigService.loginUrl(), user).pipe(
      switchMap(result => this.processTokenResult(result, rememberMe))
    );
  }

  refreshToken(): Observable<TokenResult> {
    if (this.refreshToken$) {
      return EMPTY;
    }

    const user = {
      id: Number(this.authenticationService.authenticatedUser.id),
      token: this.authenticationService.authenticatedUser.token
    };

    this.refreshToken$ = this.httpClient.post<TokenResult>(this.apiConfigService.refreshTokenUrl(), user).pipe(
      switchMap(result => this.processTokenResult(result, true).pipe(
        tap(() => this.refreshToken$ = undefined)
      )),
      catchError((err) => {
        this.refreshToken$ = undefined;
        return throwError(err);
      })
    );

    return this.refreshToken$;
  }

  private processTokenResult(tokenResult: TokenResult, rememberMe: boolean): Observable<TokenResult> {
    if (tokenResult && tokenResult.token) {
      if (this.checkAppUpdate(tokenResult.version)) {
        this.authenticationService.authSuccess(tokenResult.token, rememberMe);
        return of(tokenResult);
      } else {
        return EMPTY;
      }
    }
    return of(null);
  }

  logout(redirectPath: string = '/login') {
    this.sendLogoutRequest().subscribe(() => {
      this.logoutClient(redirectPath);
    });
  }

  private sendLogoutRequest(): Observable<any> {
    if (!this.authenticationService.authenticatedUser || !this.authenticationService.authenticatedUser.id) {
      return of(null);
    }
    return this.httpClient.post<any>(this.apiConfigService.logoutApiUrl(), null);
  }

  logoutClient(redirectPath: string = '/login') {
    this.loggedoutSubject.next(this.authenticationService.authenticatedUser);
    this.localStorageService.removeItem(this.authenticationService.authenticatedUserStorageKey);
    this.authenticationService.clearAuthenticatedUser();
    this.refreshToken$ = undefined;
    this.router.navigateByUrl(redirectPath);
  }

  public forgotPassword(authenticatedUser: AuthenticatedUser): Observable<any> {
    return this.httpClient.post(this.apiConfigService.forgotPasswordUrl(), authenticatedUser);
  }

  public changePassword(authenticatedUser: AuthenticatedUser): Observable<any> {
    return this.httpClient.post(this.apiConfigService.changePasswordUrl(), authenticatedUser);
  }

  private checkAppUpdate(version: AppVersionResult): boolean {
    if (this.appUpdate.appVersionCode && version && version.versionCode > this.appUpdate.appVersionCode() &&
      version.forceUpdateFrom > this.appUpdate.appVersionCode()) {
      this.dialogMessage.showInfoMessage('New app version has been released! Please download the update for latest features', () => {
        this.logout();
        if (this.appUpdate.downloadAppUrl) {
          window.open(this.appUpdate.downloadAppUrl);
        } else if (this.appUpdate.downloadAppFunc) {
          this.appUpdate.downloadAppFunc();
        }
      });
      return false;
    }

    return true;
  }
}
